import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonLabel,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    calendarOutline,
    chevronForwardOutline,
    locationOutline,
    peopleOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionDetail,
    CompetitionGame,
    Team,
    getCompetitionDetail,
    getHomeData
} from "../services/mexfbaApi";

import {
    StandingRow,
    createStandingsByGroup,
    isCompletedGame
} from "../utils/competitionStandings";
import {
    createInternalRatings,
    usesInternalRanking
} from "../utils/internalRanking";

import "./Results.css";

interface RouteParams {
    id: string;
}

interface TeamPosition {
    position: number;
    totalTeams: number;
}

function parseDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatDate(date?: string): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

function getToday(): Date {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return today;
}

function getCompetitionStatus(detail: CompetitionDetail): string {
    const startDate = parseDate(detail.Info.fechaI);
    const endDate = parseDate(detail.Info.fechaF);
    const today = getToday();

    if (startDate && today < startDate) {
        return "Próxima";
    }

    if (endDate && today > endDate) {
        return "Finalizada";
    }

    return "En curso";
}

function clamp(
    value: number,
    minimum: number,
    maximum: number
): number {
    return Math.max(minimum, Math.min(maximum, value));
}

function getFeaturedGameRelevance(
    game: CompetitionGame,
    teamPositions: Map<string, TeamPosition>,
    standingsByGroup: Map<string, StandingRow[]>,
    internalRatings: Map<string, number>,
    competitionName?: string
): number {
    const localPosition = teamPositions.get(game.id_local);
    const visitorPosition = teamPositions.get(game.id_visita);

    const localRating =
        internalRatings.get(String(game.id_local)) || 1100;

    const visitorRating =
        internalRatings.get(String(game.id_visita)) || 1100;

    const averageRating =
        (localRating + visitorRating) / 2;

    const ratingDifference = Math.abs(
        localRating - visitorRating
    );

    /*
        Un partido es competitivo cuando ambos equipos tienen
        niveles similares. Una diferencia de 250+ Elo no agrega
        interés competitivo.
    */
    const ratingCloseness = clamp(
        1 - ratingDifference / 250,
        0,
        1
    );

    /*
        Premia que ambos equipos tengan un nivel alto; no sólo
        que sean parejos.
    */
    const ratingQuality = clamp(
        (averageRating - 1050) / 200,
        0,
        1
    );

    const usesElo = usesInternalRanking(competitionName);

    /*
        Si no hay posiciones o algún equipo aún no ha jugado,
        la tabla todavía no es una señal confiable.
    */
    const localRow = Array.from(standingsByGroup.values())
        .flat()
        .find(function (row) {
            return row.id === game.id_local;
        });

    const visitorRow = Array.from(standingsByGroup.values())
        .flat()
        .find(function (row) {
            return row.id === game.id_visita;
        });

    const hasReliableStandings =
        Boolean(localPosition) &&
        Boolean(visitorPosition) &&
        (localRow?.played || 0) > 0 &&
        (visitorRow?.played || 0) > 0;

    if (!hasReliableStandings) {
        /*
            En jornadas iniciales se evita premiar posiciones
            generadas por desempates alfabéticos.
        */
        if (usesElo) {
            return ratingQuality * 65 + ratingCloseness * 35;
        }

        return ratingCloseness * 100;
    }

    const localStrength =
        localPosition!.position / localPosition!.totalTeams;

    const visitorStrength =
        visitorPosition!.position / visitorPosition!.totalTeams;

    const standingsCloseness = clamp(
        1 - Math.abs(localStrength - visitorStrength),
        0,
        1
    );

    /*
        Con tabla confiable, se combina cercanía en posiciones
        con nivel y paridad del ranking histórico.
    */
    if (usesElo) {
        return (
            standingsCloseness * 35 +
            ratingQuality * 40 +
            ratingCloseness * 25
        );
    }

    return standingsCloseness * 100;
}

function getGameStatus(game: CompetitionGame): string {
    if (isCompletedGame(game)) {
        return "Final";
    }

    const gameDate = parseDate(game.fecha);

    if (!gameDate) {
        return "Sin fecha";
    }

    if (gameDate < getToday()) {
        return "Pendiente";
    }

    return "Próximo";
}

function getGroupLabel(index: number): string {
    /*
        0 -> A
        1 -> B
        ...
        25 -> Z
        26 -> AA
        27 -> AB
    */
    let currentValue = index + 1;
    let label = "";

    while (currentValue > 0) {
        const remainder = (currentValue - 1) % 26;

        label =
            String.fromCharCode(65 + remainder) + label;

        currentValue = Math.floor(
            (currentValue - 1) / 26
        );
    }

    return label;
}
interface CompetitionRound {
    jornada: string;
    phase: string;
}

function getRoundFromGame(
    game: CompetitionGame
): CompetitionRound | null {
    const jornada = String(game.jornada || "").trim();

    /*
        No se puede identificar una jornada si el partido no tiene
        el campo jornada definido.
    */
    if (!jornada) {
        return null;
    }

    return {
        jornada,
        phase: String(game.partido || "grupos")
    };
}

function isSameRound(
    game: CompetitionGame,
    round: CompetitionRound
): boolean {
    const gameRound = getRoundFromGame(game);

    return (
        gameRound !== null &&
        gameRound.jornada === round.jornada &&
        gameRound.phase === round.phase
    );
}

function getCurrentCompetitionRound(
    games: CompetitionGame[],
    referenceDate: Date
): CompetitionRound | null {
    const today = new Date(referenceDate);

    today.setHours(0, 0, 0, 0);

    const dayOfWeek = today.getDay();

    /*
        Domingo = 0
        Lunes = 1
        Martes = 2
        ...
        Sábado = 6
    */
    if (dayOfWeek === 1) {
        /*
            El lunes mantiene la jornada del fin de semana anterior.
            Busca juegos entre viernes y domingo, sin importar si ya
            tienen marcador o siguen pendientes.
        */
        const lastFriday = new Date(today);

        lastFriday.setDate(today.getDate() - 3);

        const lastSunday = new Date(today);

        lastSunday.setDate(today.getDate() - 1);

        const weekendGames = games
            .filter(function (game) {
                const gameDate = parseDate(game.fecha);

                return (
                    gameDate !== null &&
                    gameDate >= lastFriday &&
                    gameDate <= lastSunday &&
                    getRoundFromGame(game) !== null
                );
            })
            .sort(function (firstGame, secondGame) {
                const firstDate =
                    parseDate(firstGame.fecha)?.getTime() || 0;

                const secondDate =
                    parseDate(secondGame.fecha)?.getTime() || 0;

                /*
                    Se prioriza el partido más reciente del fin de semana.
                */
                return secondDate - firstDate;
            });

        if (weekendGames.length > 0) {
            return getRoundFromGame(weekendGames[0]);
        }
    }

    /*
        De martes a domingo, o si no hubo partidos el fin de semana,
        se usa la jornada del próximo partido más cercano.
    */
    const nextGames = games
        .filter(function (game) {
            const gameDate = parseDate(game.fecha);

            return (
                gameDate !== null &&
                gameDate >= today &&
                !isCompletedGame(game) &&
                getRoundFromGame(game) !== null
            );
        })
        .sort(function (firstGame, secondGame) {
            const firstDate =
                parseDate(firstGame.fecha)?.getTime() || 0;

            const secondDate =
                parseDate(secondGame.fecha)?.getTime() || 0;

            return firstDate - secondDate;
        });

    if (nextGames.length > 0) {
        return getRoundFromGame(nextGames[0]);
    }

    /*
        Si la competencia ya terminó o no tiene próximos partidos,
        se conserva la jornada del último juego registrado.
    */
    const lastGames = games
        .filter(function (game) {
            const gameDate = parseDate(game.fecha);

            return (
                gameDate !== null &&
                gameDate <= today &&
                getRoundFromGame(game) !== null
            );
        })
        .sort(function (firstGame, secondGame) {
            const firstDate =
                parseDate(firstGame.fecha)?.getTime() || 0;

            const secondDate =
                parseDate(secondGame.fecha)?.getTime() || 0;

            return secondDate - firstDate;
        });

    return lastGames.length > 0
        ? getRoundFromGame(lastGames[0])
        : null;
}

const Results: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [competition, setCompetition] =
        useState<CompetitionDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [internalRatings, setInternalRatings] = useState<Map<string, number>>(
        new Map()
    );
const [selectedGroup, setSelectedGroup] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadCompetition = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const [competitionData, homeData] = await Promise.all([
                    getCompetitionDetail(id, forceRefresh),
                    getHomeData(forceRefresh)
                ]);


                setCompetition(competitionData);
                setTeams(homeData.teams);

                setInternalRatings(
                    createInternalRatings(
                        homeData.teams,
                        homeData.competitions,
                        homeData.games
                    )
                );


                const availableGroups = Array.from(
    new Set(
        Object.values(competitionData.Equipos)
            .map(function (team) {
                return team.grupo?.trim() || "";
            })
            .filter(Boolean)
            .sort(function (firstGroup, secondGroup) {
                return firstGroup.localeCompare(
                    secondGroup,
                    "es-MX",
                    {
                        numeric: true
                    }
                );
            })
    )
);

setSelectedGroup(function (currentGroup) {
    /*
        Si el usuario abre otra competencia, se evita conservar
        un grupo que no existe en la nueva competencia.
    */
    return availableGroups.includes(currentGroup)
        ? currentGroup
        : availableGroups[0] || "";
});

            } catch (requestError) {
                console.error(
                    "No fue posible cargar la competencia:",
                    requestError
                );

                setError(
                    "No fue posible cargar la información de esta competencia."
                );
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadCompetition();
        },
        [loadCompetition]
    );

    const allGames = useMemo(function (): CompetitionGame[] {
        if (!competition) {
            return [];
        }

        return [
            ...Object.values(competition.Partidos || {}),
            ...Object.values(competition.Final || {})
        ].sort(function (firstGame, secondGame) {
            const firstDate = parseDate(firstGame.fecha)?.getTime() || 0;
            const secondDate = parseDate(secondGame.fecha)?.getTime() || 0;

            return firstDate - secondDate;
        });
    }, [competition]);

    const standingsByGroup = useMemo(function () {
        if (!competition) {
            return new Map<string, StandingRow[]>();
        }

        return createStandingsByGroup(
            Object.values(competition.Equipos || {}),
            Object.values(competition.Partidos || {})
        );
    }, [competition]);

    const teamLogos = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team.logo || ""];
            })
        );
    }, [teams]);

    const teamPositions = useMemo(function () {
        const positions = new Map<string, TeamPosition>();

        standingsByGroup.forEach(function (rows) {
            rows.forEach(function (row) {
                positions.set(row.id, {
                    position: row.position,
                    totalTeams: rows.length
                });
            });
        });

        return positions;
    }, [standingsByGroup]);

    const selectedStandings = useMemo(function () {
        return standingsByGroup.get(selectedGroup) || [];
    }, [selectedGroup, standingsByGroup]);
const groupIds = useMemo(function () {
    return Array.from(standingsByGroup.keys()).sort(
        function (firstGroup, secondGroup) {
            return firstGroup.localeCompare(
                secondGroup,
                "es-MX",
                {
                    numeric: true
                }
            );
        }
    );
}, [standingsByGroup]);

const groupLabels = useMemo(function () {
    return new Map(
        groupIds.map(function (groupId, index) {
            return [groupId, getGroupLabel(index)];
        })
    );
}, [groupIds]);
const currentRound = useMemo(function (): CompetitionRound | null {
    return getCurrentCompetitionRound(
        allGames,
        getToday()
    );
}, [allGames]);

    const featuredGames = useMemo(function () {
    if (!currentRound) {
        return [];
    }

    return allGames
        .filter(function (game) {
            /*
                Sólo se incluyen partidos de la jornada actual.
                También se compara la fase para no combinar, por ejemplo,
                Jornada 1 de grupos con una ronda eliminatoria.
            */
            return isSameRound(game, currentRound);
        })
        .map(function (game) {
            const date = parseDate(game.fecha)?.getTime() || 0;

            return {
                game,
                relevance: getFeaturedGameRelevance(
                    game,
                    teamPositions,
                    standingsByGroup,
                    internalRatings,
                    competition?.Info.nombre
                ),
                date
            };
        })

        .sort(function (firstGame, secondGame) {
            if (secondGame.relevance !== firstGame.relevance) {
                return (
                    secondGame.relevance - firstGame.relevance
                );
            }

            return firstGame.date - secondGame.date;
        })
        .slice(0, 4)
        .map(function (item) {
            return item.game;
        });
}, [
    allGames,
    currentRound,
    teamPositions,
    standingsByGroup,
    internalRatings,
    competition
]);

    const recentGames = useMemo(function () {
        return allGames
            .filter(isCompletedGame)
            .sort(function (firstGame, secondGame) {
                const firstDate = parseDate(firstGame.fecha)?.getTime() || 0;
                const secondDate =
                    parseDate(secondGame.fecha)?.getTime() || 0;

                return secondDate - firstDate;
            })
            .slice(0, 5);
    }, [allGames]);

    const groupCount = groupIds.length;
    const teamCount = competition
        ? Object.keys(competition.Equipos || {}).length
        : 0;

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadCompetition(true);

        event.detail.complete();
    }

    function getTeamLogo(teamId: string): string {
        return teamLogos.get(String(teamId)) || "";
    }

    function openGame(gameId: string) {
        history.push(`/game/${gameId}`);
    }

    function openTeam(teamId: string) {
        history.push(`/team/${teamId}`);
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Competencia</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="results-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !competition) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Competencia</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="results-content">
                    <div className="results-empty-state">
                        <IonIcon icon={trophyOutline} />
                        <strong>No fue posible abrir la competencia</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadCompetition(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonHeader translucent className="results-header--translucent">
                <IonToolbar className="results-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar a competencias"
                            onClick={function () {
                                history.goBack();
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Competencia</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="results-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando competencia..."
                    />
                </IonRefresher>

                <section className="competition-header">
                    <div className="competition-header__top">
                        {competition.Info.logo ? (
                            <img
                                className="competition-header__logo"
                                src={competition.Info.logo}
                                alt={`Logo de ${competition.Info.nombre}`}
                            />
                        ) : (
                            <div className="competition-header__logo-placeholder">
                                <IonIcon icon={trophyOutline} />
                            </div>
                        )}

                        <div>
                            <IonBadge
                                color={
                                    getCompetitionStatus(competition) ===
                                    "En curso"
                                        ? "success"
                                        : "medium"
                                }
                            >
                                {getCompetitionStatus(competition)}
                            </IonBadge>

                            <h1>{competition.Info.nombre}</h1>

                            <p>
                                {competition.Info.fechaI
                                    ? `${formatDate(
                                          competition.Info.fechaI
                                      )} — ${formatDate(
                                          competition.Info.fechaF
                                      )}`
                                    : "Temporada sin fechas confirmadas"}
                            </p>
                        </div>
                    </div>

                    <div className="competition-header__facts">
                        <span>
                            <IonIcon icon={peopleOutline} />
                            {teamCount} equipos
                        </span>

                        <span>
                            <IonIcon icon={trophyOutline} />
                            {groupCount} grupos
                        </span>

                        <span>
                            <IonIcon icon={calendarOutline} />
                            {competition.Info.jornadas || "—"} jornadas
                        </span>

                        {competition.Info.sede ? (
                            <span>
                                <IonIcon icon={locationOutline} />
                                {competition.Info.sede}
                            </span>
                        ) : null}
                    </div>
                </section>
<section className="competition-navigation">
    <IonButton
        fill="outline"
        onClick={function () {
            history.push(`/calendar/${id}`);
        }}
    >
        Calendario
    </IonButton>

    <IonButton>
        Tabla
    </IonButton>

    <IonButton
        fill="outline"
        onClick={function () {
            history.push(`/statsResults/${id}`);
        }}
    >
        Jugadores
    </IonButton>

    <IonButton
        fill="outline"
        onClick={function () {
            history.push(`/statsTeamResults/${id}`);
        }}
    >
        Estadísticas
    </IonButton>
</section>

                {featuredGames.length > 0 && (
                    <section className="results-section">
                        <div className="results-section__heading">
                            <div>
                                <span>
    {currentRound
        ? `JORNADA ${currentRound.jornada}`
        : "PARTIDOS"}
</span>

<h2>Partidos destacados</h2>

                            </div>
                        </div>

                        <div className="featured-games">
                            {featuredGames.map(function (game) {
                                return (
                                    <button
                                        className="featured-game"
                                        key={game.id}
                                        type="button"
                                        onClick={function () {
                                            openGame(game.id);
                                        }}
                                    >
                                        <div className="featured-game__date">
                                            {formatDate(game.fecha)}
                                            <IonBadge color="primary">
                                                {getGameStatus(game)}
                                            </IonBadge>
                                        </div>

                                        <div className="featured-game__teams">
                                            <span>
                                                {game.nombre_local}

                                                {getTeamLogo(game.id_local) ? (
                                                    <img
                                                        src={getTeamLogo(
                                                            game.id_local
                                                        )}
                                                        alt=""
                                                    />
                                                ) : null}
                                            </span>

                                            <strong>VS</strong>

                                            <span>
                                                {getTeamLogo(game.id_visita) ? (
                                                    <img
                                                        src={getTeamLogo(
                                                            game.id_visita
                                                        )}
                                                        alt=""
                                                    />
                                                ) : null}

                                                {game.nombre_visita}
                                            </span>
                                        </div>

                                        <small>
                                            {game.sede || "Sede por confirmar"}
                                        </small>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                <section className="results-section results-section--standings">
                    <div className="results-section__heading">
                        <div>
                            <span>POSICIONES</span>
                            <h2>Tabla de grupos</h2>
                        </div>
                    </div>

                    {groupCount > 1 && (
    <IonSegment
        className="group-selector"
        value={selectedGroup}
        scrollable
        onIonChange={function (event) {
            setSelectedGroup(
                String(event.detail.value || "")
            );
        }}
    >
        {groupIds.map(function (groupId) {
            return (
                <IonSegmentButton
                    key={groupId}
                    value={groupId}
                >
                    <IonLabel>
                        Grupo {groupLabels.get(groupId)}
                    </IonLabel>
                </IonSegmentButton>
            );
        })}
    </IonSegment>
)}


                    <div className="standings-table-wrapper">
                        <table className="standings-table">
                            <thead>
                                <tr>
                                    <th>POS</th>
                                    <th>EQUIPO</th>
                                    <th>PJ</th>
                                    <th>G</th>
                                    <th>E</th>
                                    <th>P</th>
                                    <th>PF</th>
                                    <th>PC</th>
                                    <th>DIF</th>
                                </tr>
                            </thead>

                            <tbody>
                                {selectedStandings.map(function (row) {
                                    return (
                                        <tr key={row.id}>
                                            <td className="standings-table__position">
                                                {row.position}
                                            </td>

                                            <td>
                                                <button
                                                    className="standings-table__team"
                                                    type="button"
                                                    onClick={function () {
                                                        openTeam(row.id);
                                                    }}
                                                >
                                                    {getTeamLogo(row.id) ? (
                                                        <img
                                                            src={getTeamLogo(
                                                                row.id
                                                            )}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <span className="standings-table__team-placeholder">
                                                            {row.nombre
                                                                .trim()
                                                                .charAt(0)}
                                                        </span>
                                                    )}

                                                    <span>{row.nombre}</span>
                                                </button>
                                            </td>

                                            <td>{row.played}</td>
                                            <td>{row.wins}</td>
                                            <td>{row.draws}</td>
                                            <td>{row.losses}</td>
                                            <td>{row.pointsFor}</td>
                                            <td>{row.pointsAgainst}</td>
                                            <td
                                                className={
                                                    row.difference > 0
                                                        ? "standings-table__positive"
                                                        : row.difference < 0
                                                        ? "standings-table__negative"
                                                        : ""
                                                }
                                            >
                                                {row.difference > 0
                                                    ? `+${row.difference}`
                                                    : row.difference}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                {recentGames.length > 0 && (
                    <section className="results-section">
                        <div className="results-section__heading">
                            <div>
                                <span>ÚLTIMOS RESULTADOS</span>
                                <h2>Partidos finalizados</h2>
                            </div>
                        </div>

                        <div className="recent-games">
                            {recentGames.map(function (game) {
                                return (
                                    <button
                                        className="recent-game"
                                        key={game.id}
                                        type="button"
                                        onClick={function () {
                                            openGame(game.id);
                                        }}
                                    >
                                        <span className="recent-game__date">
                                            {formatDate(game.fecha)}
                                        </span>

                                        <span className="recent-game__team">
                                            {game.nombre_local}
                                        </span>

                                        <strong>
                                            {game.resLocal} - {game.resVisita}
                                        </strong>

                                        <span className="recent-game__team recent-game__team--visitor">
                                            {game.nombre_visita}
                                        </span>

                                        <IonIcon icon={chevronForwardOutline} />
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}
            </IonContent>
        </IonPage>
    );
};

export default Results;
